import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Video,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Navigation,
  User,
  Star,
  AlertTriangle,
  Award,
  UserPlus,
  History
} from "lucide-react";
import EscalationModal from "../components/EscalationModal";
import ResponderRoleReminder from "../components/ResponderRoleReminder";
import IncidentReportButton from "../components/IncidentReportButton";
import SessionFeedback from "../components/SessionFeedback";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom marker icons
const veteranIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const responderIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map bounds updater component
const MapUpdater = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

const ActiveRequest = ({ user }) => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showHandoffDialog, setShowHandoffDialog] = useState(false);
  const [handoffNote, setHandoffNote] = useState("");
  const [handoffHistory, setHandoffHistory] = useState([]);
  const [isHandingOff, setIsHandingOff] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const isVeteran = user?.role === "veteran";
  const isResponder = user?.role === "responder";

  useEffect(() => {
    loadRequest();
    loadMessages();
    
    // Poll for updates
    pollIntervalRef.current = setInterval(() => {
      loadRequest();
      loadMessages();
      if (isResponder) {
        updateResponderLocation();
      }
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [requestId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadRequest = async () => {
    try {
      const response = await axios.get(`${API}/requests/${requestId}`);
      setRequest(response.data);
      setIsLoading(false);
      
      // If completed or cancelled, stop polling
      if (["completed", "cancelled"].includes(response.data.status)) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      }
    } catch (error) {
      console.error("Error loading request:", error);
      setIsLoading(false);
      if (error.response?.status === 404) {
        navigate("/dashboard");
      }
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/requests/${requestId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const updateResponderLocation = () => {
    if (navigator.geolocation && isResponder) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await axios.post(`${API}/requests/${requestId}/update-location`, {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          } catch (error) {
            console.error("Error updating location:", error);
          }
        },
        (error) => console.error("Geolocation error:", error)
      );
    }
  };

  // Crisis keywords that trigger escalation modal
  const crisisKeywords = [
    'kill myself', 'end my life', 'suicide', 'want to die', 
    'hurt myself', 'self harm', 'no reason to live', 
    'better off dead', 'ending it all', 'can\'t go on'
  ];

  const checkForCrisisKeywords = (message) => {
    const lowerMessage = message.toLowerCase();
    return crisisKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Check for crisis keywords before sending
    if (checkForCrisisKeywords(newMessage)) {
      setShowEscalationModal(true);
    }

    try {
      await axios.post(`${API}/requests/${requestId}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage("");
      loadMessages();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/requests/${requestId}/cancel`);
      toast.success("Request cancelled");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to cancel request");
    }
  };

  const handleComplete = async () => {
    try {
      const response = await axios.post(`${API}/requests/${requestId}/complete`);
      
      // Check for new badges earned (for responders)
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach(badge => {
          toast.success(
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              <span>Badge Earned: {badge.name} {badge.icon}</span>
            </div>,
            { duration: 5000 }
          );
        });
      }
      
      // Show feedback form for veterans
      if (response.data.show_feedback && isVeteran) {
        setShowFeedback(true);
        toast.success("Support session completed. Please share your feedback!");
      } else {
        toast.success("Support session completed. Thank you!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to complete request");
    }
  };

  const handleFeedbackComplete = () => {
    setFeedbackSubmitted(true);
    setTimeout(() => {
      navigate("/dashboard");
    }, 2000);
  };

  const loadHandoffHistory = async () => {
    try {
      const response = await axios.get(`${API}/requests/${requestId}/handoff-history`);
      setHandoffHistory(response.data.handoff_history || []);
    } catch (error) {
      console.error("Failed to load handoff history:", error);
    }
  };

  const handleHandoff = async () => {
    if (!handoffNote.trim()) {
      toast.error("Please provide handoff notes for the next responder");
      return;
    }

    setIsHandingOff(true);
    try {
      await axios.post(`${API}/requests/${requestId}/handoff`, {
        note: handoffNote
      });
      toast.success("Handoff initiated. Available responders have been notified.");
      setShowHandoffDialog(false);
      setHandoffNote("");
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate handoff");
    } finally {
      setIsHandingOff(false);
    }
  };

  // Load handoff history when request is loaded
  useEffect(() => {
    if (request) {
      loadHandoffHistory();
    }
  }, [request?.request_id]);

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "pending":
        return { label: "Waiting for Responder", color: "bg-accent", icon: Clock };
      case "accepted":
        return { label: "Responder On The Way", color: "bg-secondary", icon: Navigation };
      case "in_progress":
        return { label: "Support in Progress", color: "bg-secondary", icon: CheckCircle };
      case "completed":
        return { label: "Completed", color: "bg-muted", icon: CheckCircle };
      case "cancelled":
        return { label: "Cancelled", color: "bg-destructive", icon: XCircle };
      default:
        return { label: status, color: "bg-muted", icon: Clock };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Request not found</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(request.status);
  const StatusIcon = statusInfo.icon;

  // Calculate map bounds
  const mapBounds = [];
  if (request.location?.lat && request.location?.lng) {
    mapBounds.push([request.location.lat, request.location.lng]);
  }
  if (request.responder_location?.lat && request.responder_location?.lng) {
    mapBounds.push([request.responder_location.lat, request.responder_location.lng]);
  }

  const defaultCenter = request.location?.lat 
    ? [request.location.lat, request.location.lng]
    : [39.8283, -98.5795]; // Center of US

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                {request.request_type === "in_person" ? "In-Person Support" : "Phone/Video Call"}
              </p>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${statusInfo.color}`}></span>
                <span className="text-sm text-muted-foreground">{statusInfo.label}</span>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Incident Report Button - Responders only */}
            {isResponder && (
              <IncidentReportButton 
                sessionId={requestId} 
                volunteerId={user?.user_id}
                compact
              />
            )}
            
            {/* Escalation Button - Always visible */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowEscalationModal(true)}
              className="text-destructive border-destructive/50 hover:bg-destructive/10"
              data-testid="escalation-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Crisis
            </Button>
            
            {request.status !== "completed" && request.status !== "cancelled" && (
              <>
                {isVeteran && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCancelDialog(true)}
                    data-testid="cancel-request-btn"
                  >
                    Cancel
                  </Button>
                )}
                {isResponder && (request.status === "accepted" || request.status === "in_progress") && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowHandoffDialog(true)}
                    data-testid="handoff-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Handoff
                  </Button>
                )}
                {request.status === "accepted" || request.status === "in_progress" ? (
                  <Button 
                    size="sm"
                    onClick={() => setShowCompleteDialog(true)}
                    className="bg-secondary hover:bg-secondary/90"
                    data-testid="complete-request-btn"
                  >
                    Complete
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-16 pb-20 flex flex-col lg:flex-row h-screen">
        {/* Map Section */}
        <div className="flex-1 relative" data-testid="map-container">
          {request.request_type === "in_person" ? (
            <MapContainer
              center={defaultCenter}
              zoom={13}
              className="h-[300px] lg:h-full w-full map-container"
              style={{ zIndex: 0 }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Veteran Location */}
              {request.location?.lat && (
                <Marker 
                  position={[request.location.lat, request.location.lng]}
                  icon={veteranIcon}
                >
                  <Popup>
                    <div className="text-center">
                      <p className="font-semibold">{request.veteran_name}</p>
                      <p className="text-sm text-muted-foreground">Veteran Location</p>
                    </div>
                  </Popup>
                </Marker>
              )}
              
              {/* Responder Location */}
              {request.responder_location?.lat && (
                <Marker 
                  position={[request.responder_location.lat, request.responder_location.lng]}
                  icon={responderIcon}
                >
                  <Popup>
                    <div className="text-center">
                      <p className="font-semibold">{request.responder_name}</p>
                      <p className="text-sm text-muted-foreground">Responder</p>
                    </div>
                  </Popup>
                </Marker>
              )}
              
              {mapBounds.length > 1 && <MapUpdater bounds={mapBounds} />}
            </MapContainer>
          ) : (
            <div className="h-[300px] lg:h-full w-full bg-muted flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  {request.request_type === "call" ? (
                    <Video className="w-10 h-10 text-primary" />
                  ) : (
                    <Phone className="w-10 h-10 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    {request.status === "pending" 
                      ? "Waiting for a responder..."
                      : "Ready for your call"
                    }
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Use the chat below to coordinate
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ETA Overlay */}
          {request.eta_minutes && request.status === "accepted" && request.request_type === "in_person" && (
            <div className="absolute top-4 left-4 right-4 lg:left-auto lg:right-4 lg:w-64 z-10">
              <Card className="card-tactical">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                    <Navigation className="w-5 h-5 text-secondary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Arrival</p>
                    <p className="font-bold text-lg text-foreground" style={{ fontFamily: 'Manrope' }}>
                      ~{request.eta_minutes} min
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Info & Chat Panel */}
        <div className="lg:w-[400px] border-t lg:border-t-0 lg:border-l border-border flex flex-col bg-card">
          {/* Person Info */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={isVeteran ? request.responder_picture : request.veteran_picture} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {request.status === "pending" && isVeteran ? (
                    <User className="w-6 h-6" />
                  ) : (
                    getInitials(isVeteran ? request.responder_name : request.veteran_name)
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {request.status === "pending" && isVeteran 
                    ? "Finding a Responder..."
                    : (isVeteran ? request.responder_name : request.veteran_name)
                  }
                </p>
                <Badge variant="secondary" className="text-xs">
                  {isVeteran ? "Responder" : "Veteran"}
                </Badge>
              </div>
              <StatusIcon className="w-5 h-5 text-muted-foreground" />
            </div>
            
            {request.description && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-xs label-uppercase mb-1">Notes</p>
                <p className="text-sm text-foreground">{request.description}</p>
              </div>
            )}

            {/* Role Reminder for Responders */}
            {isResponder && (
              <ResponderRoleReminder compact className="mt-4" />
            )}
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" data-testid="messages-container">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8">
                  No messages yet. Start the conversation!
                </p>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.sender_id === user?.user_id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_id === user?.user_id 
                          ? "text-primary-foreground/70" 
                          : "text-muted-foreground"
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input */}
          {request.status !== "completed" && request.status !== "cancelled" && (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  size="icon"
                  disabled={!newMessage.trim()}
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          )}

          {/* Completed/Cancelled State */}
          {(request.status === "completed" || request.status === "cancelled") && !showFeedback && (
            <div className="p-4 border-t border-border bg-muted">
              <p className="text-center text-muted-foreground text-sm">
                This support session has {request.status === "completed" ? "ended" : "been cancelled"}.
              </p>
              <Button 
                variant="outline" 
                className="w-full mt-3"
                onClick={() => navigate("/dashboard")}
              >
                Back to Dashboard
              </Button>
            </div>
          )}
          
          {/* Feedback Form for Veterans */}
          {showFeedback && isVeteran && !feedbackSubmitted && (
            <div className="p-4 border-t border-border">
              <SessionFeedback 
                requestId={requestId}
                responderName={request?.responder_name}
                onComplete={handleFeedbackComplete}
              />
            </div>
          )}
        </div>
      </main>

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Cancel Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this support request? 
              You can always create a new request if you need help again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancel}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-cancel-btn"
            >
              Cancel Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Complete Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Complete Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Mark this support session as complete? 
              Thank you for being there for each other.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not Yet</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleComplete}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="confirm-complete-btn"
            >
              Complete Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Crisis Escalation Modal */}
      <EscalationModal 
        isOpen={showEscalationModal} 
        onClose={() => setShowEscalationModal(false)}
        sessionId={requestId}
        triggerType={isResponder ? "volunteer_initiated" : "auto_detected"}
        volunteerId={isResponder ? user?.user_id : null}
      />

      {/* Handoff Dialog */}
      <Dialog open={showHandoffDialog} onOpenChange={setShowHandoffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Hand Off Session
            </DialogTitle>
            <DialogDescription>
              Transfer this support session to another available responder
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Leave detailed notes for the next responder so they can continue 
                providing seamless support to the veteran.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Handoff Notes *</label>
              <Textarea
                value={handoffNote}
                onChange={(e) => setHandoffNote(e.target.value)}
                placeholder="Describe the current situation, what's been discussed, and any important context..."
                rows={4}
                data-testid="handoff-note-input"
              />
            </div>

            {handoffHistory.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Previous Handoff Notes
                </label>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {handoffHistory.map((h, idx) => (
                    <div key={idx} className="p-2 bg-muted/50 rounded text-sm">
                      <p className="font-medium">{h.responder_name}</p>
                      <p className="text-muted-foreground">{h.handoff_note}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(h.handed_off_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHandoffDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleHandoff}
              disabled={isHandingOff || !handoffNote.trim()}
              data-testid="confirm-handoff-btn"
            >
              {isHandingOff ? "Initiating..." : "Initiate Handoff"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiveRequest;
